import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { API, API_BASE } from '../config/api';

const BACKEND_URL = API_BASE;

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [coverImage, setCoverImage] = useState('/login-bg.jpg');

  useEffect(() => {
    // Fetch custom login cover if available
    const fetchCover = async () => {
      try {
        const response = await fetch(`${API}/settings/login-cover`);
        if (response.ok) {
          const data = await response.json();
          if (data.image_url) {
            setCoverImage(`${BACKEND_URL}${data.image_url}`);
          }
        }
      } catch (error) {
        // Use default image on error
        console.debug('Using default login cover');
      }
    };
    fetchCover();
  }, []);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Login failed');
        return;
      }

      toast.success('Login successful!');
      navigate('/dashboard', { state: { user: data } });
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Unable to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Mobile Header - Visible only on mobile */}
      <div className="lg:hidden bg-gradient-to-r from-[#80298F] to-[#6B2278] px-6 py-8 text-white text-center">
        <img src="/logo.png" alt="The Ramirez Legal Group" className="h-12 mx-auto mb-3 brightness-0 invert" />
        <h1 className="text-xl font-heading font-bold">The Ramirez Legal Group</h1>
        <p className="text-sm opacity-90 mt-1">Secure Client Portal</p>
      </div>

      {/* Left side - Image (Desktop only) */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${coverImage})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#80298F]/90 to-[#80298F]/70"></div>
        <div className="relative z-10 p-12 flex flex-col justify-center text-white">
          <h1 className="text-5xl font-heading font-bold mb-4">The Ramirez Legal Group</h1>
          <p className="text-xl opacity-90">Secure client communication portal</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 w-full lg:w-1/2 flex items-center justify-center px-5 py-6 sm:p-8 bg-[#FDFCFE]">
        <div className="w-full max-w-md">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-1 sm:mb-2" data-testid="login-heading">Welcome Back</h2>
            <p className="text-sm sm:text-base text-gray-600">Sign in to access your cases</p>
          </div>

          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 sm:h-10 text-base"
                  placeholder="your.email@example.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#80298F] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11 sm:h-10 text-base"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 sm:h-10 bg-[#80298F] hover:bg-[#6B2278] text-white text-base"
              disabled={isLoading}
              data-testid="login-submit-button"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="relative my-5 sm:my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[#FDFCFE] text-gray-500">Or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-11 sm:h-10 border-2 border-gray-300 hover:border-[#80298F] text-base"
            data-testid="google-login-button"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>

          <p className="mt-5 sm:mt-6 text-center text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="font-medium text-[#80298F] hover:text-[#6B2278]" data-testid="register-link">
              Register with invitation code
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;